import type { ReactNode } from 'react'
import { createContext, startTransition, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api, impersonationStore, tokenStore } from '@/lib/api'
import { queryClient } from '@/lib/queryClient'
import { disablePush } from '@/lib/push'
import type { User } from '@/types'

interface Credentials {
  email: string
  password: string
}

interface RegisterPayload {
  name: string
  email: string
  password: string
  password_confirmation: string
}

/**
 * Who an admin is about to sign in as. The kind picks the endpoint (the team
 * and customer routes each refuse the other's accounts) and the admin screen
 * to come back to.
 */
export interface ImpersonationTarget {
  id: number
  kind: 'team' | 'customer'
}

const IMPERSONATION_ROUTES: Record<ImpersonationTarget['kind'], { endpoint: (id: number) => string; returnTo: string }> = {
  team: { endpoint: (id) => `/admin/users/${id}/impersonate`, returnTo: '/admin/staff' },
  customer: { endpoint: (id) => `/admin/customers/${id}/impersonate`, returnTo: '/admin/customers' },
}

interface AuthValue {
  user: User | null
  loading: boolean
  /** True while this tab is an admin "signed in as" a team member or a customer. */
  impersonating: boolean
  login: (credentials: Credentials) => Promise<User>
  register: (payload: RegisterPayload) => Promise<User>
  completeOAuth: (code: string) => Promise<User>
  logout: () => Promise<void>
  refresh: () => Promise<void>
  /** Become that account and land on its home: a console for the team, /account for a customer. */
  impersonate: (target: ImpersonationTarget) => Promise<User>
  /** Back to the admin's own session, landing on the admin screen the session started from. */
  endImpersonation: () => Promise<void>
}

const AuthContext = createContext<AuthValue | null>(null)

function unwrapUser(data: any): User {
  return (data?.data ?? data) as User
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [impersonating, setImpersonating] = useState(() => !!impersonationStore.get())
  const navigate = useNavigate()

  const refresh = useCallback(async () => {
    if (!tokenStore.get()) {
      setUser(null)
      setLoading(false)
      return
    }
    // Decided before the request: on a 401 the api interceptor has already
    // dropped the impersonation slot and is sending the tab back to the
    // admin screen it came from, so the admin's own token must survive this
    // catch.
    const wasImpersonating = !!impersonationStore.get()
    try {
      const { data } = await api.get('/auth/me')
      setUser(unwrapUser(data))
    } catch {
      if (!wasImpersonating) {
        tokenStore.clear()
        setUser(null)
      }
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  /**
   * Query keys carry no user id, so cached data would follow an identity
   * switch: the staff bell showing the admin's notifications, or the admin
   * seeing the staff member's after coming back. The cache is dropped in
   * the same tick as the user swap, before the next console mounts, so its
   * queries start fresh. Doing it in an effect instead would run after that
   * mount and orphan the queries already in flight, leaving the page on its
   * skeleton for ever.
   *
   * The optional destination is applied inside the same transition as the
   * user swap, and that is not a convenience. react-router runs navigate()
   * as a transition, a lower priority lane than a plain state update, so
   * set separately React commits the new user on the OLD route first;
   * RequireAuth there answers the changed role with its own redirect, and
   * that redirect lands after ours and wins. One transition means one
   * render, so the guard only ever sees the new user on the new route.
   */
  const become = useCallback(
    (nextUser: User | null, nowImpersonating: boolean, next?: string) => {
      queryClient.clear()
      startTransition(() => {
        setImpersonating(nowImpersonating)
        setUser(nextUser)
        if (next) navigate(next, { replace: true })
      })
    },
    [navigate],
  )

  const login = useCallback(async (credentials: Credentials) => {
    impersonationStore.clear()
    const { data } = await api.post('/auth/login', credentials)
    tokenStore.set(data.token)
    const nextUser = unwrapUser(data.user)
    become(nextUser, false)
    return nextUser
  }, [become])

  /**
   * Second half of Google sign-in: the backend redirected here with a
   * single-use code rather than the token itself, so the token never appears
   * in a URL, in history, or in a Referer header.
   */
  const completeOAuth = useCallback(async (code: string) => {
    impersonationStore.clear()
    const { data } = await api.post('/auth/google/exchange', { code })
    tokenStore.set(data.token)
    const { data: me } = await api.get('/auth/me')
    const nextUser = unwrapUser(me)
    become(nextUser, false)
    return nextUser
  }, [become])

  // Registration is instant - the API returns a token, so the customer is signed in right away.
  const register = useCallback(async (payload: RegisterPayload) => {
    impersonationStore.clear()
    const { data } = await api.post('/auth/register', payload)
    tokenStore.set(data.token)
    const nextUser = unwrapUser(data.user)
    become(nextUser, false)
    return nextUser
  }, [become])

  /**
   * Admin > Staff or admin > Customers > "Sign in as". That account's token
   * goes into the per-tab slot, with the screen to come back to; the admin's
   * own token stays in localStorage for the way back. The impersonate
   * response cannot say who is impersonating (that request still ran as the
   * admin), so /auth/me is read with the new token.
   */
  const impersonate = useCallback(async (target: ImpersonationTarget) => {
    const route = IMPERSONATION_ROUTES[target.kind]
    const { data } = await api.post(route.endpoint(target.id))
    impersonationStore.set(data.token, route.returnTo)
    try {
      const { data: me } = await api.get('/auth/me')
      const nextUser = unwrapUser(me)
      become(nextUser, true, roleHome(nextUser))
      return nextUser
    } catch (err) {
      impersonationStore.clear()
      throw err
    }
  }, [become])

  /** Back to the admin's own session. Resolves only once the admin user is loaded again. */
  const endImpersonation = useCallback(async () => {
    // Read before the slot is cleared: it goes with the token.
    const returnTo = impersonationStore.returnTo()
    try {
      // Deletes the account's short-lived token on the server.
      await api.post('/auth/logout')
    } catch {
      // already expired or revoked - the slot is cleared either way
    }
    impersonationStore.clear()
    try {
      const { data } = await api.get('/auth/me')
      become(unwrapUser(data), false, returnTo)
    } catch {
      // The admin's own token died while parked (password reset, suspension):
      // nothing to go back to, so it is a plain sign-in.
      tokenStore.clear()
      become(null, false)
    }
  }, [become])

  const logout = useCallback(async () => {
    if (impersonationStore.get()) {
      // Signing out of a "signed in as" session means going back to being
      // the admin, not signing the admin out. Push is skipped on purpose:
      // this device's subscription belongs to the admin, not the member.
      await endImpersonation()
      return
    }
    try {
      // This device only - other devices keep their push, matching the
      // token-scoped server logout. Must run while the token still works.
      await disablePush()
    } catch {
      // no subscription, or push unsupported - nothing to remove
    }
    try {
      await api.post('/auth/logout')
    } catch {
      // token might already be invalid; clear anyway
    }
    tokenStore.clear()
    become(null, false)
  }, [become, endImpersonation])

  const value = useMemo(
    () => ({
      user,
      loading,
      impersonating,
      login,
      register,
      completeOAuth,
      logout,
      refresh,
      impersonate,
      endImpersonation,
    }),
    [user, loading, impersonating, login, register, completeOAuth, logout, refresh, impersonate, endImpersonation],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}

export function roleHome(user: User | null): string {
  if (!user) return '/login'
  if (user.role === 'admin') return '/admin'
  if (user.role === 'staff') return '/staff'
  if (user.role === 'customer') return '/account'
  if (user.role === 'partner') return '/partner'
  if (user.role === 'sales') return '/sales'
  // Any unrecognised role lands on the public storefront, the only home that
  // cannot redirect-loop with RequireAuth.
  return '/'
}
